import { useQuery } from '@apollo/client/react';
import { GET_MY_CASES, GET_MY_INQUIRY } from '../graphql/clientQueries';
import type { Case, Inquiry, CaseStatus } from '../types';

interface UseClientCasesReturn {
  /** List of client's cases */
  cases: Case[];
  /** List of client's inquiries */
  inquiries: Inquiry[];
  /** Whether the client has active cases (status not INQUIRY) */
  hasActiveCases: boolean;
  /** Whether the client has pending inquiries */
  hasPendingInquiries: boolean;
  /** Whether data is loading */
  isLoading: boolean;
  /** Refetch both cases and inquiries */
  refetch: () => void;
}

interface GetMyCasesResponse {
  listMyCases: Case[];
}

interface GetMyInquiryResponse {
  getMyInquiry: Inquiry | null;
}

/**
 * useClientCases - Client's cases and inquiries hook
 * 
 * Fetches the authenticated client's cases and inquiries using client-specific
 * GraphQL queries that enforce data isolation at the backend.
 * 
 * Computes:
 * - hasActiveCases: true if any case has status other than INQUIRY (Requirement 2.2)
 * - hasPendingInquiries: true if inquiries exist (Requirement 2.3)
 * 
 * These flags are used by ClientNavigation to determine the case tab label:
 * - "My Case" when hasActiveCases is true (Requirement 2.2)
 * - "My Inquiry" when only hasPendingInquiries is true (Requirement 2.3)
 * - Dropdown when both are true (Requirement 2.4)
 * 
 * Requirements: 2.2, 2.3, 2.4
 */
export function useClientCases(): UseClientCasesReturn {
  // Fetch client's cases
  const {
    data: casesData,
    loading: casesLoading,
    refetch: refetchCases,
  } = useQuery<GetMyCasesResponse>(GET_MY_CASES, {
    fetchPolicy: 'cache-and-network',
  });

  // Fetch client's inquiry
  const {
    data: inquiryData,
    loading: inquiryLoading,
    refetch: refetchInquiry,
  } = useQuery<GetMyInquiryResponse>(GET_MY_INQUIRY, {
    fetchPolicy: 'cache-and-network',
  });

  // Extract cases from response, default to empty array
  const cases = casesData?.listMyCases ?? [];

  // Extract inquiries - convert single inquiry to array for consistency
  const inquiries: Inquiry[] = inquiryData?.getMyInquiry 
    ? [inquiryData.getMyInquiry] 
    : [];

  // Compute hasActiveCases: cases with status other than INQUIRY (Requirement 2.2)
  const hasActiveCases = cases.some(
    (caseItem) => caseItem.status !== ('INQUIRY' as CaseStatus)
  );

  // Compute hasPendingInquiries: any inquiries exist (Requirement 2.3)
  const hasPendingInquiries = inquiries.length > 0;

  // Combined loading state
  const isLoading = casesLoading || inquiryLoading;

  // Refetch function that refreshes both queries
  const refetch = () => {
    refetchCases();
    refetchInquiry();
  };

  return {
    cases,
    inquiries,
    hasActiveCases,
    hasPendingInquiries,
    isLoading,
    refetch,
  };
}
